import React, { useState, useCallback, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { StandardFieldProps } from './FieldTypes';
import HelperText from './HelperText';

/** Check if string looks like a valid image URL. */
function isImageUrl(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string' || !value.trim()) return false;
  try {
    const url = new URL(value.trim());
    return /^https?:$/i.test(url.protocol);
  } catch {
    return false;
  }
}

const StandardImageUrlField = ({
  control,
  fieldName,
  field,
  readOnly,
}: StandardFieldProps) => {
  const [imgError, setImgError] = useState(false);
  const watchedValue = useWatch({ control, name: fieldName });
  useEffect(() => setImgError(false), [watchedValue]);
  const onImgError = useCallback(() => setImgError(true), []);
  const onImgLoad = useCallback(() => setImgError(false), []);

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { isTouched, error },
      }) => {
        const url = value && typeof value === 'string' ? value.trim() : '';
        const showImage = isImageUrl(url) && !imgError;

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              fullWidth
              id={fieldName}
              name={fieldName}
              label={field.label}
              placeholder={field.placeholder}
              value={value ?? ''}
              disabled={readOnly}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              error={isTouched && Boolean(error)}
              inputProps={{ 'aria-describedby': `helper-text-${fieldName}` }}
            />
            {showImage && (
              <Box
                component="img"
                src={url}
                alt={field.label ?? 'Preview'}
                onError={onImgError}
                onLoad={onImgLoad}
                sx={{
                  maxWidth: 200,
                  maxHeight: 200,
                  objectFit: 'contain',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
            )}
            <HelperText
              name={fieldName}
              error={isTouched && error?.message}
              description={field.description}
            />
          </Box>
        );
      }}
    />
  );
};

export default StandardImageUrlField;
